import React, { useState, useEffect } from 'react';
import { getCapiLogs, getCapiLogsStats } from '../services/api';
import { Search, RefreshCw, CheckCircle, XCircle, AlertCircle, Loader2, ExternalLink, Filter } from 'lucide-react';

const MetaLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [filters, setFilters] = useState({ status: '', phone: '' });
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: 20, offset, ...filters };
      Object.keys(params).forEach(key => { if (!params[key]) delete params[key]; });
      const response = await getCapiLogs(params);
      setLogs(response.data.data);
      setPagination({ total: response.data.total, limit: response.data.limit, offset });
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getCapiLogsStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Erro stats:', error);
    }
  };

  useEffect(() => { 
    fetchLogs(); 
    fetchStats();
  }, []);

  const handleFilter = () => {
    fetchLogs(0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <span className="flex items-center gap-1 text-green-400"><CheckCircle size={16} /> Enviado</span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-red-400"><XCircle size={16} /> Falhou</span>;
      case 'no_ctwa':
        return <span className="flex items-center gap-1 text-yellow-400"><AlertCircle size={16} /> Sem CTWA</span>;
      case 'lead_not_found':
        return <span className="flex items-center gap-1 text-gray-400"><AlertCircle size={16} /> Lead não encontrado</span>;
      default:
        return <span className="text-gray-400">{status}</span>;
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Meta CAPI Logs</h1>
          <p className="text-gray-400">Histórico de envios para a API de Conversões da Meta</p>
        </div>
        <button 
          onClick={() => { fetchLogs(); fetchStats(); }} 
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          <RefreshCw size={20} /> Atualizar
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <p className="text-gray-400 text-sm">Hoje</p>
            <p className="text-2xl font-bold text-white">{stats.today?.total || 0}</p>
            <p className="text-green-400 text-sm">{stats.today?.success || 0} enviados</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <p className="text-gray-400 text-sm">Valor Hoje</p>
            <p className="text-2xl font-bold text-green-400">R$ {parseFloat(stats.today?.total_value || 0).toFixed(2)}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <p className="text-gray-400 text-sm">Sucesso</p>
            <p className="text-2xl font-bold text-green-400">
              {stats.byStatus?.find(s => s.status === 'success')?.count || 0}
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4">
            <p className="text-gray-400 text-sm">Sem CTWA</p>
            <p className="text-2xl font-bold text-yellow-400">
              {stats.byStatus?.find(s => s.status === 'no_ctwa')?.count || 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-gray-400 text-sm mb-1 block">Telefone</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input 
                type="text" 
                value={filters.phone} 
                onChange={(e) => setFilters({...filters, phone: e.target.value})} 
                placeholder="Buscar por telefone..." 
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white" 
              />
            </div>
          </div>
          <div className="min-w-[150px]">
            <label className="text-gray-400 text-sm mb-1 block">Status</label>
            <select 
              value={filters.status} 
              onChange={(e) => setFilters({...filters, status: e.target.value})} 
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="">Todos</option>
              <option value="success">Enviado</option>
              <option value="failed">Falhou</option>
              <option value="no_ctwa">Sem CTWA</option>
              <option value="lead_not_found">Lead não encontrado</option>
            </select>
          </div>
          <button 
            onClick={handleFilter} 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
          >
            <Filter size={20} /> Filtrar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-blue-500" size={40} />
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm border-b border-gray-700 bg-gray-900/50">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Produto</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3">WABA</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3">fbtrace_id</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-20 text-center text-gray-500">
                      Nenhum log encontrado
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr 
                      key={log.id} 
                      className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-4 py-3">
                        <span className="text-white text-sm">
                          {new Date(log.created_at).toLocaleDateString('pt-BR')}
                        </span>
                        <br />
                        <span className="text-gray-500 text-xs">
                          {new Date(log.created_at).toLocaleTimeString('pt-BR')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-white font-medium">{log.customer_name || '-'}</span>
                        <br />
                        <span className="text-gray-500 text-xs">{log.customer_phone || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-sm max-w-[200px] truncate">
                        {log.product_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-green-400 font-medium">
                        R$ {parseFloat(log.value || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-400 text-xs">{log.instance_name || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(log.status)}
                      </td>
                      <td className="px-4 py-3">
                        {log.fbtrace_id ? (
                          <span className="text-blue-400 text-xs font-mono">{log.fbtrace_id.substring(0, 15)}...</span>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                <span className="text-gray-400 text-sm">
                  Página {currentPage} de {totalPages} ({pagination.total} registros)
                </span>
                <div className="flex gap-2">
                  <button 
                    onClick={() => fetchLogs(pagination.offset - pagination.limit)} 
                    disabled={currentPage <= 1} 
                    className="px-3 py-1 bg-gray-700 rounded text-white disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button 
                    onClick={() => fetchLogs(pagination.offset + pagination.limit)} 
                    disabled={currentPage >= totalPages} 
                    className="px-3 py-1 bg-gray-700 rounded text-white disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Detalhes */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedLog(null)}>
          <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Detalhes do Log #{selectedLog.id}</h2>
              <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm">Status</label>
                  <div className="text-white">{getStatusBadge(selectedLog.status)}</div>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Data/Hora</label>
                  <p className="text-white">{new Date(selectedLog.created_at).toLocaleString('pt-BR')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm">Cliente</label>
                  <p className="text-white">{selectedLog.customer_name || '-'}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Telefone</label>
                  <p className="text-white">{selectedLog.customer_phone || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm">Produto</label>
                  <p className="text-white">{selectedLog.product_name || '-'}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Valor</label>
                  <p className="text-green-400 font-bold">R$ {parseFloat(selectedLog.value || 0).toFixed(2)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm">Plataforma</label>
                  <p className="text-white">{selectedLog.platform || '-'}</p>
                </div>
                <div>
                  <label className="text-gray-400 text-sm">Transaction ID</label>
                  <p className="text-white text-sm font-mono">{selectedLog.transaction_id || '-'}</p>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-semibold mb-2">Dados Meta CAPI</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">WABA ID</label>
                    <p className="text-white font-mono text-sm">{selectedLog.waba_id || '-'}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Dataset ID</label>
                    <p className="text-white font-mono text-sm">{selectedLog.dataset_id || '-'}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <label className="text-gray-400 text-sm">Instance</label>
                  <p className="text-white">{selectedLog.instance_name || '-'}</p>
                </div>
                <div className="mt-2">
                  <label className="text-gray-400 text-sm">fbtrace_id</label>
                  <p className="text-blue-400 font-mono text-sm">{selectedLog.fbtrace_id || '-'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="text-gray-400 text-sm">Canal de Envio</label>
                    <p className={`font-semibold text-sm ${selectedLog.channel === 'dataset' ? 'text-purple-400' : selectedLog.channel === 'pixel' ? 'text-green-400' : 'text-blue-400'}`}>{(selectedLog.channel || 'capi').toUpperCase()}</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Pixel Utilizado</label>
                    <p className="text-white font-mono text-sm">{selectedLog.pixel_id || '-'}</p>
                  </div>
                </div>
              </div>

              {selectedLog.payload && (
                <div className="border-t border-gray-700 pt-4">
                  <label className="text-gray-400 text-sm">Payload do Evento (enviado à Meta)</label>
                  <pre className="bg-gray-900 rounded-lg p-3 mt-1 text-green-400 text-xs overflow-x-auto max-h-64 overflow-y-auto">
                    {(() => { try { return JSON.stringify(typeof selectedLog.payload === 'string' ? JSON.parse(selectedLog.payload) : selectedLog.payload, null, 2); } catch (e) { return String(selectedLog.payload); } })()}
                  </pre>
                </div>
              )}

              {selectedLog.ctwa_clid && (
                <div className="border-t border-gray-700 pt-4">
                  <label className="text-gray-400 text-sm">CTWA Click ID</label>
                  <p className="text-white font-mono text-xs break-all bg-gray-900 p-2 rounded">{selectedLog.ctwa_clid}</p>
                </div>
              )}

              {selectedLog.error_message && (
                <div className="border-t border-gray-700 pt-4">
                  <label className="text-gray-400 text-sm">Erro</label>
                  <p className="text-red-400 bg-red-900/20 p-2 rounded">{selectedLog.error_message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaLogs;
